/// <reference types="google.maps" />
import React from 'react';
import { Alert } from '@mui/material';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Button, Input, Select } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import { assetUrl } from '../../../../utils/webConfig';
import { countries } from '../../../../utils/statesConstant';

const countryOptions = countries.map((c) => ({ value: c.alpha2, label: c.name }));

type AddressFormValues = {
    zip: string;
    countryCode: string;
};

type AuthAddressStepViewProps = {
    register: UseFormRegister<AddressFormValues>;
    errors: FieldErrors<AddressFormValues>;
    loading?: boolean;
    street: string;
    streetError?: string;
    zipValue: string;
    countryValue: string;
    predictions: google.maps.places.AutocompletePrediction[];
    showSuggestions: boolean;
    handleStreetChange: React.ChangeEventHandler<HTMLInputElement>;
    handleSuggestionSelect: (p: google.maps.places.AutocompletePrediction) => void;
    hideSuggestions: () => void;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    error?: string | null;
    dismissError?: () => void;
};

export const AuthAddressStepView: React.FC<AuthAddressStepViewProps> = ({
    register,
    errors,
    loading,
    street,
    streetError,
    zipValue,
    countryValue,
    predictions,
    showSuggestions,
    handleStreetChange,
    handleSuggestionSelect,
    hideSuggestions,
    onSubmit,
    error,
    dismissError,
}) => (
    <form id="auth-address-form" onSubmit={onSubmit}>
        <div
            id="auth-address-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
        >
            <div
                id="auth-address-heading"
                className="text-body-large text-primary mb-1"
            >
                Billing Address
            </div>
            <div
                id="auth-address-security-message"
                className="flex items-center gap-2 mb-6 text-body-xsmall text-secondary"
            >
                <img
                    id="auth-address-security-lock-icon"
                    src={assetUrl('/images/svg/lock-icon.svg')}
                    alt=""
                    width={12}
                    height={14}
                />
                Your information is secure
            </div>
            <div id="auth-address-street-container" className="relative mb-3">
                <Input
                    id="auth-address-street"
                    label="Street"
                    value={street}
                    onChange={handleStreetChange}
                    onBlur={hideSuggestions}
                    error={!!streetError}
                    helperText={streetError}
                />
                {showSuggestions && predictions.length > 0 && (
                    <ul
                        id="auth-address-suggestions"
                        className="absolute z-50 w-full bg-white border border-separator rounded-lg shadow-lg mt-1 overflow-hidden"
                    >
                        {predictions.map((p) => (
                            <li
                                id={`auth-address-suggestion-${p.place_id}`}
                                key={p.place_id}
                                className="px-4 py-3 text-body-small text-primary cursor-pointer hover:bg-gray-50"
                                onMouseDown={() => handleSuggestionSelect(p)}
                            >
                                {p.structured_formatting.main_text}
                                {p.structured_formatting.secondary_text && (
                                    <span
                                        id={`auth-address-suggestion-secondary-${p.place_id}`}
                                        className="text-secondary ml-1"
                                    >
                                        {p.structured_formatting.secondary_text}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div id="auth-address-region-fields" className="flex gap-2 mb-3">
                <Input
                    id="auth-address-postal-code"
                    {...register('zip', {
                        required: '*Please enter a valid Zip/Postal Code',
                        minLength: {
                            value: 5,
                            message: '*Please enter a valid Zip/Postal Code',
                        },
                    })}
                    value={zipValue}
                    label="Zip/Postal Code"
                    inputMode="numeric"
                    maxLength={7}
                    error={!!errors.zip}
                    helperText={errors.zip?.message}
                />
                <Select
                    id="auth-address-country"
                    {...register('countryCode', { required: true })}
                    label="Country"
                    value={countryValue}
                    options={countryOptions}
                    error={!!errors.countryCode}
                />
            </div>
            {error ? (
                <Alert
                    id="auth-address-error-alert"
                    className="mt-6"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            <Button
                id="auth-address-next-button"
                type="submit"
                className="w-full mt-auto"
                loading={loading}
            >
                Next
            </Button>
        </div>
    </form>
);
